import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { resolveImage } from "@/lib/api";

/**
 * Customers Also Bought — real purchase-signal-based recommendations.
 * Backend: GET /products/{id}/customers-also-bought — aggregates order history
 * for co-purchases, falls back to same-category if a product has no order data yet.
 * Visually consistent with Moon's Pairings but sourced from real orders.
 */
export default function CustomersAlsoBought({ product }) {
  const [items, setItems] = useState([]);
  const [source, setSource] = useState("");

  useEffect(() => {
    if (!product?.id) return;
    api.get(`/products/${product.id}/customers-also-bought`)
      .then(({ data }) => { setItems(data.products || []); setSource(data.source || ""); })
      .catch(() => setItems([]));
  }, [product?.id]);

  if (!items.length) return null;

  return (
    <section className="mt-12" data-testid="also-bought-section" aria-labelledby="also-bought-title">
      <div className="overline">Trending together</div>
      <h2 id="also-bought-title" className="font-display text-xl sm:text-2xl font-light text-sj-ink mt-2">
        Customers Also Bought
      </h2>
      <p className="mt-1 text-xs text-sj-muted">
        {source === "purchases"
          ? "Frequently bought alongside this piece by other Star Jewels customers."
          : "Handpicked from the same collection."}
      </p>

      <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-5">
        {items.map((p) => (
          <Link
            key={p.id}
            to={`/product/${p.id}`}
            className="group block"
            data-testid={`also-bought-item-${p.id}`}
          >
            <div className="aspect-square bg-white overflow-hidden border border-sj-ink/8 rounded-md">
              {p.images?.[0] ? (
                <img
                  src={resolveImage(p.images[0], { w: 400 })}
                  alt={`${p.name} — ${p.category} — Star Jewels artificial jewelry`}
                  className="w-full h-full object-contain p-2 group-hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                  decoding="async"
                />
              ) : null}
            </div>
            <div className="mt-2 text-sm text-sj-ink line-clamp-2 group-hover:text-sj-goldDark transition-colors">{p.name}</div>
            <div className="mt-0.5 text-sm font-medium text-sj-ink">₹{Number(p.price).toLocaleString("en-IN")}</div>
          </Link>
        ))}
      </div>
    </section>
  );
}
